/* ==========================================================================
   Blenders Online 2026 · bo-finder.js · "Find your blender" (sections/bo-finder.liquid, looks: bo-finder.css)
   Three quick questions that slide sideways on one panel, then "Your match": the live products of the
   answer's collection, fetched with the Section Rendering API (<collection url + filters>&section_id=bo-finder),
   so prices, stars and stock are the store's own. Fewer than two available products = the wider range
   (the collection without its filters, then the previous answer's collection, then the section's
   last-resort collection). Nothing is stored; nothing personal is sent anywhere.
   The vortex lines (bo-fx.js) spin up with every answer and keep turning faster while the match loads.
   Deferred, idempotent, re-binds on shopify:section:load, follows the theme editor's block selection.
   ========================================================================== */
(() => {
  'use strict';
  const W = window, D = document;
  if (W.BOFinder) return;

  const reduced = () => W.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const OUT_MS = 300, PRESS_MS = 240;

  /* ---------- the match: one request per address, shared by every finder on the page ---------- */
  const cache = new Map();
  const load = (url) => {
    if (!url) return Promise.reject(new Error('no address'));
    if (cache.has(url)) return cache.get(url);
    const ask = url + (url.indexOf('?') > -1 ? '&' : '?') + 'section_id=bo-finder';
    const p = fetch(ask, { credentials: 'same-origin', headers: { Accept: 'text/html' } })
      .then((r) => { if (!r.ok) throw new Error('HTTP ' + r.status); return r.text(); })
      .then((html) => {
        const box = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-bo-finder-result]');
        if (!box) throw new Error('no result');
        const num = (v, d) => { const n = parseInt(v, 10); return isNaN(n) ? d : n; };
        const count = num(box.dataset.count, 0);
        return { url, count, home: num(box.dataset.nHome, count), biz: num(box.dataset.nBiz, count), title: box.dataset.title || '', items: Array.from(box.querySelectorAll('.bo-finder__item')) };
      });
    cache.set(url, p);
    p.catch(() => cache.delete(url));
    return p;
  };

  /* fix-home 01-10 (critic 25): who a path is for. A home path hides machines marked Commercial only, a business
     path hides machines marked Household / Personal only; a machine with no marking fits both. */
  const fits = (li, aud) => {
    const u = (li.dataset.aud || '').toLowerCase();
    if (!aud || !u) return true;
    const c = u.indexOf('commercial') > -1, h = u.indexOf('household') > -1 || u.indexOf('personal') > -1;
    return aud === 'home' ? (!c || h) : aud === 'business' ? (!h || c) : true;
  };
  const countFor = (r, aud) => (aud === 'home' ? r.home : aud === 'business' ? r.biz : r.count);

  const fill = (text, map) => Object.keys(map).reduce((t, k) => t.split('[' + k + ']').join(map[k]), text || '');
  const svg = (d) => '<svg class="bo-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><path d="' + d + '"/></svg>';

  class Finder {
    constructor(root) {
      this.root = root;
      root.boFinder = this;
      const $ = (s) => root.querySelector(s);
      this.t = root.dataset;
      this.stage = $('[data-stage]');
      this.results = $('[data-results]');
      this.grid = $('[data-grid]');
      this.note = $('[data-note]');
      this.foot = $('[data-foot]');
      this.all = $('[data-all]');
      this.allLabel = $('[data-all-label]');
      this.trail = $('[data-trail]');
      this.dots = $('[data-dots]');
      this.stepno = $('[data-stepno]');
      this.restartBtn = $('.bo-finder__progress [data-restart]');
      this.live = $('[data-live]');
      this.lines = root.dataset.lines ? D.querySelector(root.dataset.lines) : null;
      this.limit = parseInt(root.dataset.limit, 10) || 4;

      this.steps = new Map();
      root.querySelectorAll('[data-step]').forEach((s) => this.steps.set(s.dataset.after, s));
      this.byKey = new Map();
      root.querySelectorAll('.bo-finder__opt').forEach((b) => { if (b.dataset.key && !this.byKey.has(b.dataset.key)) this.byKey.set(b.dataset.key, b); });
      this.first = this.steps.get('') || root.querySelector('[data-step]');
      this.path = [];
      this.current = this.first;
      this.req = 0;
      this.total = this.reach(this.first);

      this.progress();
      this.onClick = (e) => this.click(e);
      root.addEventListener('click', this.onClick);
      root.classList.add('is-ready');
    }

    stepOf(btn) { return btn ? btn.closest('[data-step]') : null; }
    depth(step) {
      let n = 1, a = step && step.dataset.after, guard = 0;
      while (a && guard++ < 20) {
        const b = this.byKey.get(a);
        if (!b) break;
        n++;
        a = this.stepOf(b).dataset.after;
      }
      return n;
    }
    /* fix-home 01-10 (critic 42): the most questions this step's answers can still lead to (a path that ends after
       question 2 never promises a third) */
    reach(step, guard = 0) {
      let m = this.depth(step);
      if (!step || guard > 20) return m;
      step.querySelectorAll('.bo-finder__opt').forEach((b) => { const nx = this.steps.get(b.dataset.key); if (nx && nx !== step) m = Math.max(m, this.reach(nx, guard + 1)); });
      return m;
    }
    aud(btn) {
      if (btn && btn.dataset.aud) return btn.dataset.aud;
      for (let k = this.path.length - 1; k >= 0; k--) if (this.path[k].dataset.aud) return this.path[k].dataset.aud;
      return '';
    }
    fx() { try { return this.lines && W.BOFx ? W.BOFx.get(this.lines) : null; } catch (e) { return null; } }
    spin(mult, ms) { const f = this.fx(); if (f && f.spinUp) f.spinUp(mult, ms); }
    hold(on) { const f = this.fx(); if (f && f.hold) f.hold(on); }
    say(text) { if (this.live) { this.live.textContent = ''; setTimeout(() => { this.live.textContent = text; }, 60); } }
    countText(n) { return n === 1 ? this.t.tN1 : n > 1 ? fill(this.t.tN, { count: n }) : ''; }

    click(e) {
      const opt = e.target.closest('.bo-finder__opt');
      if (opt && this.root.contains(opt)) return this.choose(opt);
      if (e.target.closest('[data-back]')) return this.back();
      if (e.target.closest('[data-restart]')) return this.restart();
      const go = e.target.closest('[data-goto]');
      if (go) { const b = this.path[+go.dataset.goto]; if (b) this.show(this.stepOf(b), 'back'); }
    }

    choose(btn) {
      if (this.pending) return;
      const step = this.stepOf(btn);
      step.querySelectorAll('.bo-finder__opt').forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
      this.path = this.path.slice(0, this.depth(step) - 1);
      this.path.push(btn);
      this.spin(5, 800);
      const next = this.steps.get(btn.dataset.key);
      const go = () => { this.pending = 0; if (next) this.show(next, 'fwd'); else this.match(btn); };
      if (reduced()) go(); else this.pending = setTimeout(go, PRESS_MS);
    }

    back() {
      if (this.current === this.results) { const b = this.path[this.path.length - 1]; if (b) this.show(this.stepOf(b), 'back'); return; }
      const a = this.current && this.current.dataset.after;
      const b = a ? this.byKey.get(a) : null;
      this.show(b ? this.stepOf(b) : this.first, 'back');
    }

    restart() {
      this.req++;
      this.hold(false);
      this.path = [];
      this.root.querySelectorAll('.bo-finder__opt[aria-pressed="true"]').forEach((b) => b.setAttribute('aria-pressed', 'false'));
      this.show(this.first, 'back');
    }

    show(step, dir, quiet) {
      if (!step) return;
      const from = this.current;
      if (from === step) { this.progress(); return; }
      if (from === this.results) { this.req++; this.hold(false); }   // leaving the match: a late answer is dropped
      const calm = reduced();
      if (from) {
        clearTimeout(from.boOut);
        from.classList.remove('is-in', 'is-back');
        if (calm) from.hidden = true;
        else {
          from.classList.add('is-out');
          from.classList.toggle('is-back', dir === 'back');
          from.boOut = setTimeout(() => { from.hidden = true; from.classList.remove('is-out', 'is-back'); }, OUT_MS);
        }
      }
      clearTimeout(step.boOut);
      step.classList.remove('is-out', 'is-back');
      step.hidden = false;
      if (!calm) {
        step.classList.add('is-in');
        step.classList.toggle('is-back', dir === 'back');
        clearTimeout(step.boIn);
        step.boIn = setTimeout(() => step.classList.remove('is-in', 'is-back'), 1400);
      }
      this.current = step;
      this.progress();
      if (!quiet) {
        const h = step.querySelector('.bo-finder__q');
        if (h) h.focus({ preventScroll: true });
        this.keepInView();
      }
      if (step !== this.results) {
        this.say((this.stepno ? this.stepno.textContent + '. ' : '') + (step.querySelector('.bo-finder__q') || {}).textContent);
        this.counts(step);
      }
    }

    /* the panel's top stays on screen when a step changes height (a phone, mostly) */
    keepInView() {
      const r = this.root.querySelector('.bo-finder__panel').getBoundingClientRect();
      // phone review 02-10: clear of the sticky header (70px on a phone), not 12px from the top of the screen under it
      const hdr = D.querySelector('[data-bo-header]'), top = (hdr ? Math.max(0, hdr.getBoundingClientRect().bottom) : 0) + 12;
      if (r.top < top - 52) W.scrollBy({ top: r.top - top, behavior: reduced() ? 'auto' : 'smooth' });
    }

    progress() {
      const onResults = this.current === this.results;
      if (!onResults) this.total = this.reach(this.current);
      else this.total = Math.max(1, this.path.length);
      const d = onResults ? this.total + 1 : this.depth(this.current);
      if (this.dots && this.dots.children.length !== this.total) this.dots.innerHTML = '<i></i>'.repeat(this.total);
      if (this.dots) Array.from(this.dots.children).forEach((i, k) => { i.classList.toggle('is-on', k === d - 1); i.classList.toggle('is-done', k < d - 1); });
      if (this.stepno) {
        this.stepno.textContent = fill(this.t.tStep, { n: Math.min(d, this.total), total: this.total });
        this.stepno.hidden = onResults;
      }
      if (this.restartBtn) this.restartBtn.hidden = d < 2 || onResults;   // the match has its own, beside See all
    }

    /* live counts on the answers that lead straight to a match (fetched once, reused by the match) */
    counts(step) {
      step.querySelectorAll('.bo-finder__opt[data-leaf]').forEach((b) => {
        const slot = b.querySelector('[data-n]');
        if (!b.dataset.url || !slot || slot.textContent) return;
        const a = this.aud(b);
        load(b.dataset.url).then((r) => { slot.textContent = this.countText(countFor(r, a)); }).catch(() => {});
      });
    }

    async match(btn) {
      const token = ++this.req;
      this.trail.innerHTML = this.path.map((b, i) => {
        const label = (b.querySelector('.bo-finder__label') || b).textContent.trim();
        const safe = label.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
        return '<li><button type="button" class="bo-chip" data-goto="' + i + '" aria-label="' + safe + ' (' + (this.t.tChange || 'change') + ')">'
          + svg('m5.5 12.5 4.2 4.2 8.8-9.4') + '<span>' + safe + '</span></button></li>';
      }).join('');
      this.note.hidden = true;
      this.foot.hidden = true;
      this.grid.setAttribute('aria-busy', 'true');
      this.grid.innerHTML = '<ul class="bo-finder__grid" role="list">' + '<li><span class="bo-finder__ghost"><span class="bo-skeleton"></span><span class="bo-skeleton"></span><span class="bo-skeleton"></span></span></li>'.repeat(this.limit) + '</ul>';
      this.show(this.results, 'fwd');
      this.hold(true);

      const parent = this.path.length > 1 ? this.path[this.path.length - 2] : null;
      const tries = [btn.dataset.url, btn.dataset.base, parent && parent.dataset.url, parent && parent.dataset.base, this.t.fallback]
        .filter((u, i, a) => u && a.indexOf(u) === i);
      const aud = this.aud(btn);
      let got = null, at = -1, best = null, bestItems = [], items = [];
      for (let i = 0; i < tries.length; i++) {
        try {
          const r = await load(tries[i]);
          if (token !== this.req) return;
          const ok = r.items.filter((li) => fits(li, aud));
          if (!best || ok.length > bestItems.length) { best = r; bestItems = ok; }
          if (ok.length >= 2) { got = r; items = ok; at = i; break; }
        } catch (e) { if (token !== this.req) return; }
      }
      if (!got && best && bestItems.length) { got = best; items = bestItems; at = tries.indexOf(best.url); }
      if (token !== this.req) return;
      this.hold(false);
      this.spin(3, 600);
      this.grid.setAttribute('aria-busy', 'false');

      if (!got) {
        this.grid.innerHTML = '<p class="bo-finder__note bo-finder__error bo-small">' + svg('M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 8v5M12 16.2v.1') + '<span></span></p>';
        this.grid.querySelector('span').textContent = this.t.tError || '';
        this.all.href = btn.dataset.url || this.t.fallback;
        this.allLabel.textContent = fill(this.t.tAll, { count: '', collection: '' }).replace(/\s+/g, ' ').replace(/\s+in\s*$/, '').trim();
        this.foot.hidden = false;
        this.say(this.t.tError || '');
        return;
      }

      // the match is a rail: four across on computers, swipe on phones (the system's own rail and card)
      const rail = D.createElement('div');
      rail.className = 'bo-rail bo-finder__rail';
      rail.setAttribute('data-bo-rail', '');
      rail.innerHTML = '<ul class="bo-rail__track" role="list"></ul><div class="bo-rail__foot"><span class="bo-rail__dots" data-bo-dots aria-hidden="true"></span>'
        + '<div class="bo-rail__nav"><button class="bo-iconbtn" type="button" data-bo-prev>' + svg('M19.5 12h-15M10.5 6l-6 6 6 6') + '</button>'
        + '<button class="bo-iconbtn" type="button" data-bo-next>' + svg('M4.5 12h15M13.5 6l6 6-6 6') + '</button></div></div>';
      rail.querySelector('[data-bo-prev]').setAttribute('aria-label', this.t.tPrev || 'Previous');
      rail.querySelector('[data-bo-next]').setAttribute('aria-label', this.t.tNext || 'Next');
      const track = rail.firstChild;
      const shown = items.slice(0, this.limit);
      shown.forEach((li, i) => {
        const n = D.importNode(li, true);
        n.classList.add('is-new');
        n.style.setProperty('--i', i);
        track.appendChild(n);
      });
      const label = fill(this.t.tAll, { count: got.count, collection: got.title });
      // fewer than four: the empty places become one "see them all" tile, so the row still ends in a way on
      const tile = shown.length < 4;
      if (tile) {
        const more = D.createElement('li');
        more.className = 'bo-finder__item bo-finder__more is-new';
        more.style.setProperty('--i', shown.length);
        more.style.setProperty('--span', String(4 - shown.length));   // fills the rest of the row on computers
        more.innerHTML = '<div class="bo-finder__more-in bo-panel bo-panel--navy"><span class="bo-finder__more-icon">' + svg('M3.5 5.5h7v7h-7zM13.5 5.5h7v7h-7zM3.5 15.5h7v3h-7zM13.5 15.5h7v3h-7z') + '</span>'
          + '<a class="bo-btn bo-finder__all" href=""><span></span>' + svg('M4.5 12h15M13.5 6l6 6-6 6').replace('bo-icon', 'bo-icon bo-icon--nudge') + '</a></div>';
        more.querySelector('a').href = got.url;
        more.querySelector('a span').textContent = label;
        const help = this.foot.querySelector('.bo-finder__help');
        if (help) more.firstChild.appendChild(help.cloneNode(true));
        track.appendChild(more);
      }
      this.grid.innerHTML = '';
      this.grid.appendChild(rail);
      if (W.BO && W.BO.init) W.BO.init(rail);

      if (at > 0) {
        this.note.innerHTML = svg('M12 3.5a8.5 8.5 0 1 0 0 17 8.5 8.5 0 0 0 0-17zM12 11v5.5M12 7.8v.1') + '<span></span>';
        this.note.querySelector('span').textContent = this.t.tWider || '';
        this.note.hidden = false;
      }
      this.all.href = got.url;
      this.allLabel.textContent = label;
      this.foot.classList.toggle('is-tiled', tile);
      this.foot.hidden = false;
      this.say(fill(this.t.tShown, { shown: shown.length, count: countFor(got, aud) }));
    }

    /* theme editor: selecting an answer block opens the question it belongs to */
    select(block) {
      const btn = block.closest('.bo-finder__opt') || block.querySelector('.bo-finder__opt');
      const step = this.stepOf(btn);
      if (!step) return;
      const path = [];
      let a = step.dataset.after, guard = 0;
      while (a && guard++ < 20) { const b = this.byKey.get(a); if (!b) break; path.unshift(b); a = this.stepOf(b).dataset.after; }
      this.path = path;
      this.root.querySelectorAll('.bo-finder__opt').forEach((b) => b.setAttribute('aria-pressed', path.indexOf(b) > -1 ? 'true' : 'false'));
      this.show(step, 'fwd', true);
    }

    destroy() { this.req++; clearTimeout(this.pending); this.root.removeEventListener('click', this.onClick); delete this.root.boFinder; }
  }

  const init = (scope) => {
    const root = scope || D;
    const list = root.matches && root.matches('[data-bo-finder]') ? [root] : Array.from(root.querySelectorAll('[data-bo-finder]'));
    list.forEach((el) => { if (!el.boFinder) new Finder(el); });
  };
  W.BOFinder = { init, load };
  if (D.readyState === 'loading') D.addEventListener('DOMContentLoaded', () => init()); else init();
  D.addEventListener('shopify:section:load', (e) => init(e.target));
  D.addEventListener('shopify:section:unload', (e) => e.target.querySelectorAll('[data-bo-finder]').forEach((el) => el.boFinder && el.boFinder.destroy()));
  D.addEventListener('shopify:block:select', (e) => {
    const root = e.target.closest && e.target.closest('[data-bo-finder]');
    if (root && root.boFinder) root.boFinder.select(e.target);
  });
})();
